import {
  EventUpdate,
  ShiftEventAndUpdateEvent,
  useEventUpdate,
  BaseEvent,
  ShiftEvent,
  EquipmentIdType,
  CreateMaintenanceNotification,
  PlannerGroup,
  useRequiredConfig
} from '@domain/shifthandover'
import {WorkCenterSearchContainer} from '@hconnect/common/components'
import {useEquipment} from '@hconnect/common/hooks/useEquipment'
import {WorkCenter} from '@hconnect/common/types'
import {validateAll, ValidatorConfig} from '@hconnect/common/validators'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {Close, FileUpload} from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Box
} from '@mui/material'
import React, {useState, useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../consts'
import {usePlannerGroups} from '../hooks'
import {sapPm} from '../translations'
import {getFormPrimaryButtonSx, getTypeSpecificValidatorConfig} from '../utils'

import {EquipmentSearchContainer} from './eventProperties'

type ErrorCase = 'conflictError' | 'validationError' | 'unknownError'

type DialogProps = {
  open: boolean
  event: ShiftEvent
  onClose: () => void
  onConfirm: (notification: CreateMaintenanceNotification) => void
  plantId: string
  onSapPmNotification?: (timezone: string) => void
}

const translate = sapPm.dialog.equipmentUpdate

export const EquipmentUpdateDialog: React.FC<DialogProps> = ({
  open,
  event,
  onClose,
  onConfirm,
  plantId,
  onSapPmNotification
}) => {
  const {i18n, t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const [settings, setSettings] = useState<Partial<BaseEvent> | null>(null)
  const [error, setError] = useState<ErrorCase | null>(null)
  const [plannerGroup, setPlannerGroup] = useState<string>('')
  const {workCenters, isEquipmentAvailable} = useRequiredConfig(plantId)
  const isFunctionalLocationOptional = useCommonFeatureFlag(
    FeatureFlagKey.FunctionalLocationOptional,
    {
      plantId
    }
  )
  const isOverlapValidationEnabled = useCommonFeatureFlag(FeatureFlagKey.StoppageOverlapValidation)

  // try to determine default work center
  const mergedEvent = {...event, ...settings}
  const {equipment, id: eventId} = mergedEvent
  const equipmentId = equipment?.id
  const equipmentIdType = equipment?.idType
  const timezone = useUpmPlantTimezone(plantId)
  const {data: plannerGroups} = usePlannerGroups(plantId)
  const equipmentPlain = useEquipment(
    plantId,
    equipmentId as string,
    equipmentIdType as EquipmentIdType,
    i18n.language,
    {
      enabled: Boolean(equipmentId && equipmentIdType)
    }
  )

  const [selectedWorkCenter, setSelectedWorkCenter] = useState<WorkCenter | undefined>()

  const updateAction = useEventUpdate(
    {
      plantId,
      timezone
    },
    {
      onError: (updateError) => {
        if (updateError.response?.status === 409) {
          setError('conflictError')
        } else {
          setError('unknownError')
        }
      }
    }
  )

  useEffect(() => {
    const defaultWorkCenter = workCenters.find(
      (wc) => wc.id === equipmentPlain.data?.defaultWorkCenterId
    )
    setSelectedWorkCenter(defaultWorkCenter)
  }, [equipmentPlain.data?.defaultWorkCenterId, workCenters])

  useEffect(() => {
    const plannerGroupId = equipmentPlain.data?.defaultPlannerGroup
    setPlannerGroup(plannerGroupId ?? '')
  }, [equipmentPlain.data?.defaultPlannerGroup])

  const onExit = () => {
    setSettings(null)
    setError(null)
  }

  const onSend = () => {
    const attachmentIds = event.attachments?.map((a) => a.id)

    if (!settings) {
      onConfirm({
        sourceEventId: eventId,
        workCenterId: selectedWorkCenter?.id,
        plannerGroupId: plannerGroup,
        attachmentIds
      })
      return
    }

    const validatorConfig: ValidatorConfig<ShiftEventAndUpdateEvent> =
      getTypeSpecificValidatorConfig(
        t,
        mergedEvent.eventType,
        isEquipmentAvailable && !isFunctionalLocationOptional,
        undefined,
        !isOverlapValidationEnabled
      )
    const validationError = validateAll<ShiftEventAndUpdateEvent>(
      validatorConfig,
      mergedEvent as BaseEvent
    )

    if (validationError.size !== 0) {
      setError('validationError')
    }

    onSapPmNotification?.(timezone)

    updateAction.mutate(
      {event: mergedEvent as unknown as EventUpdate},
      {
        onSuccess: () => {
          onConfirm({
            sourceEventId: eventId,
            workCenterId: selectedWorkCenter?.id,
            plannerGroupId: plannerGroup,
            attachmentIds
          })
        }
      }
    )
  }

  const onEquipmentChange = (next: Partial<BaseEvent>) => {
    setSettings(!next || !next.equipment ? null : next)
  }

  const renderPlannerGroup = ({plannerGroupId, plannerGroupName}: PlannerGroup) => {
    const optionLabel = `${plannerGroupId} - ${plannerGroupName}`
    return (
      <MenuItem key={plannerGroupId} value={plannerGroupId}>
        {optionLabel}
      </MenuItem>
    )
  }

  return (
    <Dialog
      TransitionProps={{
        onExit
      }}
      open={open}
      maxWidth="sm"
      data-test-id="equipment-update-dialog"
      onClose={onClose}
    >
      <DialogTitle>
        {t(event.equipment ? t('sapPm.label.workCenterQuestion') : translate.header)}
      </DialogTitle>
      <DialogContent>
        {error ? (
          t(translate[error])
        ) : (
          <>
            {!event.equipment && (
              <>
                {t(translate.body)}
                <EquipmentSearchContainer
                  plantId={plantId}
                  value={event}
                  updateMultiPart={onEquipmentChange}
                  hideMainEquipmentField
                  required
                />
                {t('sapPm.label.workCenterQuestion')}
              </>
            )}
            <WorkCenterSearchContainer
              required={false}
              t={t}
              workCenter={selectedWorkCenter}
              handleWorkCenterSelection={(workCenter) => setSelectedWorkCenter(workCenter)}
              workCenters={workCenters}
            />
            <Box pt={2}>
              <TextField
                data-test-id={'equipment-update-dialog-planner-group'}
                select
                value={plannerGroup}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPlannerGroup(event.target.value ?? '')
                }}
                fullWidth
                label={t('sapPm.label.plannerGroup')}
              >
                <MenuItem value={''}>&nbsp;</MenuItem>
                {plannerGroups?.map(renderPlannerGroup)}
              </TextField>
            </Box>
            {event.attachments && event.attachments.length > 0 && (
              <div className="pt-2" data-test-id="equipment-update-dialog-attachments">
                <div>
                  {event.attachments.length}{' '}
                  {event.attachments.length === 1 ? 'attachment' : 'attachments'}
                </div>
                {event.attachments.map((attachment) => (
                  <div key={attachment.id} data-test-id="equipment-update-dialog-attachment-name">
                    {attachment.fileName}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<Close />}
          variant="text"
          onClick={onClose}
          data-test-id="sap-pm-cancel-mn"
        >
          {t('action.cancel')}
        </Button>
        {!error && (
          <Button
            onClick={() => onSend()}
            startIcon={<FileUpload />}
            color="primary"
            variant="text"
            disabled={!mergedEvent.equipment}
            sx={getFormPrimaryButtonSx}
            data-test-id="sap-pm-send-mn"
          >
            {t('sapPm.action.createMaintenanceNotification')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
